import React, { useState, useEffect } from "react";
import { Container } from "reactstrap";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../../../components/SearchBar";
import { SportsBettingAPI } from "../../../../api/sportsBetting";
import { appendText, formatDateTime } from "../../../../constants/Helper";
import SPINNER from "../../../../components/Spinner";
import { SportsBettingResponse } from "../../../../constants/responses";
import { TableCol, TableRow } from "../../../../components/TableElements";
import { FaCopy, FaEdit, FaTrash } from "react-icons/fa";
import { CasinoAPI } from "../../../../api/casino";
import { openCloneSelectionModal } from "../../../../components/modals/CloneSelectionModal";

const SportsBettingLists = () => {
  document.title = "Casino24 - Sportsbetting List";

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 30;
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const filteredRecords = data.filter((record: any) => {
    return (
      (statusFilter === "all" ||
        record.status.toLowerCase() === statusFilter.toLowerCase()) &&
      (categoryFilter === "all" ||
        record.category.value.toLowerCase() === categoryFilter.toLowerCase()) &&
      (searchQuery === "" ||
        record.contentName.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  });
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await SportsBettingAPI.getAll();
      if (res.data) {
        const sports = res.data;
        const sortedSports = Array.isArray(sports)
          ? sports.sort(
              (a, b) => parseInt(a.homeSequence) - parseInt(b.homeSequence)
            )
          : [];
        setData(sortedSports);
      }
    } catch (error) {
      alert("Something went wrong. Try Again!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, categoryFilter]);

  const handleDelete = async (id: number) => {
    setLoading(true);
    if (window.confirm("Are you sure you want to delete this sport?")) {
      try {
        const res = await SportsBettingAPI.deleteSport(id);
        if (res.success) {
          setData((prevData) => prevData.filter((record) => record.id !== id));
        } else {
          alert("Something went wrong. Try Again");
        }
      } catch (error) {
        alert("Something went wrong. Try Again");
      }
    }
    setLoading(false);
  };

  const handleCloneSelection = (sport: SportsBettingResponse) => {
    openCloneSelectionModal({
      handleClone: handleClone,
      data: sport,
    });
  };
  const handleClone = async (
    sport: SportsBettingResponse,
    destination: string | null
  ) => {
    if (!destination) return;
    setLoading(true);
    if (destination === "sportsbetting") {
      const dataToSend = {
        ...sport,
        contentName: appendText(sport.contentName, " - Clone"),

        homeSequence:
          sport.homeSequence === "" ? "1000000" : sport.homeSequence,
      };

      try {
        const res = await SportsBettingAPI.saveSport(dataToSend);
        if (res.data.id) {
          fetchData();
        }
      } catch (error) {
        alert("Something went wrong D! Try Again.");
      }
    } else if (destination === "casinos") {
      const dataToSend = {
        ...sport,
        contentName: appendText(sport.contentName, " - Clone"),
        category: { value: "Online Casino", label: "Online Casino" },
        homeSequence:
          sport.homeSequence === "" ? "1000000" : sport.homeSequence,
      };

      try {
        const res = await CasinoAPI.saveCasino(dataToSend);
        if (res.data.id) {
          fetchData();
        }
      } catch (error) {
        alert("Something went wrong D! Try Again.");
      }
    }

    setLoading(false);
  };

  return (
    <Container
      fluid
      className="p-0 m-0 flex flex-col text-black w-full h-screen bg-[#eeeeee] overflow-scroll relative"
    >
      {/* Header */}
      <section className="p-4 text-[3xl] w-full h-fit bg-white font-bold">
        <h2>Sports Betting Lists</h2>
      </section>

      {/* Data Section */}
      <section className="flex-1 p-4 justify-center items-center">
        {/* Search and Filters */}
        <section className="bg-white mb-3 w-full overflow-x-scroll p-2">
          <h2 className="border-b pb-2 pl-3 mb-3 text-gray-600 w-full text-xl">
            Filter
          </h2>

          <div className="flex flex-col md:flex-row items-start md:items-center justify-center md:justify-between">
            {/* Status */}
            <div className="flex w-full md:max-w-md items-center justify-start mb-2 md:mb-0">
              <div className="flex flex-col">
                <label
                  htmlFor="status"
                  className="block text-[0.8rem] text-gray-400 w-full items-center justify-start flex mb-1"
                >
                  Status
                </label>
                <select
                  required
                  id="status"
                  name="status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="py-2 px-4 rounded-md w-full text-[0.8rem]"
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              {/* Category */}
              <div className="flex flex-col ml-3">
                <label
                  htmlFor="category"
                  className="block text-[0.8rem] text-gray-400 w-full items-center justify-start flex mb-1"
                >
                  Category
                </label>
                <select
                  required
                  id="category"
                  name="category"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="py-2 px-4 rounded-md w-full text-[0.8rem]"
                >
                  <option value="all">All</option>
                  <option value="Online Sports Betting">
                    Online Sports Bettings
                  </option>
                </select>
              </div>
            </div>
            <SearchBar
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          </div>

          <div className="w-full flex"></div>
        </section>
        {/* Table */}
        <section className="bg-white w-full overflow-x-scroll">
          {loading ? (
            <div className="flex justify-center items-center py-10">
              Loading...
            </div>
          ) : (
            <table className="w-full border text-[0.8rem]">
              <thead>
                <tr>
                  <TableCol value="Registration Time" />
                  <TableCol value="Category" />
                  <TableCol value="Content Name" />
                  <TableCol value="Website" />
                  <TableCol value="Email" />
                  <TableCol value="Logo" />
                  <TableCol value="Status" />
                  <TableCol value="Actions" />
                </tr>
              </thead>
              <tbody>
                {currentRecords.map((record: any, index: number) => (
                  <tr key={index}>
                    <TableRow value={formatDateTime(record.registrationTime)} />
                    <TableRow value={record.category.value} />
                    <TableRow value={record.contentName} />
                    <TableRow>
                      <a
                        href={record.website}
                        className="text-blue-500"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {record.website}
                      </a>
                    </TableRow>
                    <TableRow value={record.email} />
                    <TableRow>
                      <img
                        src={record.picture}
                        alt={record.contentName}
                        className="w-10 h-10 object-cover"
                      />
                    </TableRow>
                    <TableRow>
                      <span
                        className={`px-2 py-1 rounded ${
                          record.status === "active"
                            ? "text-green-500"
                            : "text-red-500"
                        }`}
                      >
                        {record.status}
                      </span>
                    </TableRow>
                    <TableRow>
                      <button
                        className="text-blue-500 px-2 py-1 rounded mr-2"
                        onClick={() =>
                          navigate("/vips/add-edit-sport", {
                            state: { id: record.id },
                          })
                        }
                      >
                        <FaEdit size={15} />
                      </button>
                      <button
                        className="text-red-500 px-2 py-1 rounded"
                        onClick={() => handleDelete(record.id)}
                      >
                        <FaTrash size={15} />
                      </button>
                      <button
                        className="text-gray-500 px-2 py-1 rounded"
                        onClick={() => handleCloneSelection(record)}
                      >
                        <FaCopy size={15} />
                      </button>
                    </TableRow>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
        {totalPages > 1 && (
          <div className="flex text-sm justify-end items-center mt-2 px-2">
            Page
            {Array.from({ length: totalPages }, (_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPage(index + 1)}
                className={`px-1 py-1 mx-1 ${
                  currentPage === index + 1 ? "text-blue-500" : "text-black/30"
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        )}
      </section>
      {loading && (
        <div className="absolute inset-0 bg-gray-300/30 flex items-center justify-center backdrop-blur-sm">
          <SPINNER />
        </div>
      )}
    </Container>
  );
};

export default SportsBettingLists;
